import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';
import { eq } from 'tio-ui/utilities';

// ---------------------------------------------------------------------------
// Button – reimagined, zero-Frontile button with polish & loading states
// ---------------------------------------------------------------------------

/**
 * Visual intent/severity options
 */
export type ButtonIntent = 'primary' | 'danger' | 'warning' | 'success' | 'default';

/**
 * Appearance options
 */
export type ButtonAppearance = 'default' | 'outlined' | 'minimal';

/**
 * Size options
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonSignature {
  Args: {
    /** HTML button type */
    type?: 'button' | 'submit' | 'reset';
    /** Visual intent */
    intent?: ButtonIntent;
    /** Appearance variant */
    appearance?: ButtonAppearance;
    /** Size */
    size?: ButtonSize;
    /** Show loading state (disables button, shows loading indicator) */
    isRunning?: boolean;
    /** Disabled state */
    isDisabled?: boolean;
    /** Icon component (Phosphor icon etc.) */
    icon?: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
    /** Icon size override */
    iconSize?: number;
    /** Text label */
    label?: string;
    /** Accessible label for screen readers (important for icon-only buttons) */
    ariaLabel?: string;
    /** Icon placement */
    iconPlacement?: 'start' | 'end';
    /** Full width */
    isFullWidth?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

// ---------------------------------------------------------------------------
// Style maps (Tailwind classes only)
// ---------------------------------------------------------------------------

const INTENT_FILLED: Record<ButtonIntent, string> = {
  primary:
    'bg-[#006c9a] text-white shadow-sm hover:bg-[#005a82] active:bg-[#004d6f] focus-visible:ring-[#006c9a]/40',
  danger:
    'bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500/40',
  warning:
    'bg-amber-500 text-white shadow-sm hover:bg-amber-600 active:bg-amber-700 focus-visible:ring-amber-500/40',
  success:
    'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 active:bg-emerald-800 focus-visible:ring-emerald-500/40',
  default:
    'bg-gray-100 text-gray-800 shadow-sm hover:bg-gray-200 active:bg-gray-300 dark:hover:bg-gray-600 focus-visible:ring-gray-400/40',
};

const INTENT_OUTLINED: Record<ButtonIntent, string> = {
  primary:
    'border border-[#006c9a] text-[#006c9a] bg-transparent hover:bg-[#006c9a]/5 active:bg-[#006c9a]/10 focus-visible:ring-[#006c9a]/40',
  danger:
    'border border-red-600 text-red-600 bg-transparent hover:bg-red-50 active:bg-red-100 focus-visible:ring-red-500/40 dark:hover:bg-red-950 dark:active:bg-red-900',
  warning:
    'border border-amber-500 text-amber-600 bg-transparent hover:bg-amber-50 active:bg-amber-100 focus-visible:ring-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-950',
  success:
    'border border-emerald-600 text-emerald-600 bg-transparent hover:bg-emerald-50 active:bg-emerald-100 focus-visible:ring-emerald-500/40 dark:text-emerald-400 dark:hover:bg-emerald-950',
  default:
    'border border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50 active:bg-gray-100 focus-visible:ring-gray-400/40',
};

const INTENT_MINIMAL: Record<ButtonIntent, string> = {
  primary:
    'text-[#006c9a] bg-transparent hover:bg-[#006c9a]/5 active:bg-[#006c9a]/10 focus-visible:ring-[#006c9a]/40',
  danger:
    'text-red-600 bg-transparent hover:bg-red-50 active:bg-red-100 focus-visible:ring-red-500/40 dark:hover:bg-red-950',
  warning:
    'text-amber-600 bg-transparent hover:bg-amber-50 active:bg-amber-100 focus-visible:ring-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-950',
  success:
    'text-emerald-600 bg-transparent hover:bg-emerald-50 active:bg-emerald-100 focus-visible:ring-emerald-500/40 dark:text-emerald-400 dark:hover:bg-emerald-950',
  default:
    'text-gray-700 bg-transparent hover:bg-gray-100 active:bg-gray-200 focus-visible:ring-gray-400/40',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'text-sm px-3 py-1.5 rounded-md gap-1.5',
  md: 'text-sm px-4 py-2 rounded-lg gap-2',
  lg: 'text-base px-6 py-3 rounded-lg gap-2.5',
};

const ICON_SIZES: Record<ButtonSize, number> = {
  sm: 16,
  md: 18,
  lg: 22,
};

// Loading dot colors per intent
const LOADING_DOT_FILLED: Record<ButtonIntent, string> = {
  primary: 'bg-white/80',
  danger: 'bg-white/80',
  warning: 'bg-white/80',
  success: 'bg-white/80',
  default: 'bg-gray-500',
};

const LOADING_DOT_OUTLINED: Record<ButtonIntent, string> = {
  primary: 'bg-[#006c9a]',
  danger: 'bg-red-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  default: 'bg-gray-400',
};

/**
 * Button – A polished, zero-dependency button component.
 *
 * Replaces `@frontile/buttons` with an identical API surface plus:
 * - Animated three-dot loading indicator
 * - Subtle shadow and transition polish
 * - Full dark mode support
 *
 * @example Basic usage
 * ```gts
 * import Button from 'tio-reimagined/components/button';
 *
 * <Button @intent="primary">Save Changes</Button>
 * ```
 *
 * @example With icon
 * ```gts
 * <Button @intent="primary" @icon={{PhFloppyDisk}} @label="Save" />
 * ```
 *
 * @example Loading state
 * ```gts
 * <Button @intent="primary" @isRunning={{this.isSaving}}>
 *   Save
 * </Button>
 * ```
 */
export default class Button extends Component<ButtonSignature> {
  get intent(): ButtonIntent {
    return this.args.intent ?? 'default';
  }

  get appearance(): ButtonAppearance {
    return this.args.appearance ?? 'default';
  }

  get size(): ButtonSize {
    return this.args.size ?? 'md';
  }

  get isDisabled(): boolean {
    return this.args.isDisabled || this.args.isRunning || false;
  }

  get iconSize(): number {
    return this.args.iconSize ?? ICON_SIZES[this.size] ?? 18;
  }

  get sizeClasses(): string {
    return SIZE_CLASSES[this.size] ?? SIZE_CLASSES['md'];
  }

  get intentClasses(): string {
    const intent = this.intent;
    switch (this.appearance) {
      case 'outlined':
        return INTENT_OUTLINED[intent] ?? INTENT_OUTLINED['default'];
      case 'minimal':
        return INTENT_MINIMAL[intent] ?? INTENT_MINIMAL['default'];
      default:
        return INTENT_FILLED[intent] ?? INTENT_FILLED['default'];
    }
  }

  get loadingDotClass(): string {
    const intent = this.intent;
    if (this.appearance === 'default') {
      return LOADING_DOT_FILLED[intent] ?? LOADING_DOT_FILLED['default'];
    }
    return LOADING_DOT_OUTLINED[intent] ?? LOADING_DOT_OUTLINED['default'];
  }

  get buttonClasses(): string {
    const base =
      'relative inline-flex items-center justify-center font-medium transition-all duration-200 ease-out select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';
    const disabled = this.isDisabled
      ? 'opacity-60 cursor-not-allowed pointer-events-none'
      : 'cursor-pointer';
    const width = this.args.isFullWidth ? 'w-full' : '';

    return [base, this.sizeClasses, this.intentClasses, disabled, width].filter(Boolean).join(' ');
  }

  <template>
    <button
      type={{if @type @type "button"}}
      class={{this.buttonClasses}}
      disabled={{this.isDisabled}}
      aria-busy={{if @isRunning "true"}}
      aria-label={{@ariaLabel}}
      data-test-button
      data-test-button-intent={{this.intent}}
      data-test-button-appearance={{this.appearance}}
      data-test-button-size={{this.size}}
      ...attributes
    >
      {{! Loading overlay – three pulsing dots }}
      {{#if @isRunning}}
        <span
          class="absolute inset-0 flex items-center justify-center gap-1"
          aria-label="Loading"
          role="status"
          data-test-button-loading
        >
          <span
            class="inline-block w-1.5 h-1.5 rounded-full animate-[pulse_1.4s_ease-in-out_infinite]
              {{this.loadingDotClass}}"
          ></span>
          <span
            class="inline-block w-1.5 h-1.5 rounded-full animate-[pulse_1.4s_ease-in-out_0.2s_infinite]
              {{this.loadingDotClass}}"
          ></span>
          <span
            class="inline-block w-1.5 h-1.5 rounded-full animate-[pulse_1.4s_ease-in-out_0.4s_infinite]
              {{this.loadingDotClass}}"
          ></span>
        </span>
      {{/if}}

      {{! Content – fades out when loading }}
      <span
        data-test-button-content
        class="inline-flex items-center justify-center transition-opacity duration-300 gap-2
          {{if @isRunning 'opacity-0 pointer-events-none' 'opacity-100'}}"
      >
        {{#if @icon}}
          {{#if (eq @iconPlacement "end")}}
            {{#if @label}}
              <span>{{@label}}</span>
            {{else}}
              {{yield}}
            {{/if}}
            {{#let @icon as |Icon|}}
              <Icon @size={{this.iconSize}} />
            {{/let}}
          {{else}}
            {{#let @icon as |Icon|}}
              <Icon @size={{this.iconSize}} />
            {{/let}}
            {{#if @label}}
              <span>{{@label}}</span>
            {{else}}
              {{yield}}
            {{/if}}
          {{/if}}
        {{else if @label}}
          <span>{{@label}}</span>
        {{else}}
          {{yield}}
        {{/if}}
      </span>
    </button>
  </template>
}
